import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, MapPin, Users, Clock, Star, Ticket, Bell } from 'lucide-react';
import { api } from '../../api';

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  endDate?: string;
  location: string;
  venue?: string;
  category: string;
  attendees?: number;
  price?: number | string;
  speakers?: string[];
  image: string;
  highlights: string[];
  isPopular?: boolean;
  views?: number;
  rating?: number;
}

interface NewsArticle {
  id: string;
  title: string;
  description: string;
  date: string;
  image: string;
  category: string;
}

const ShowcaseEvenements: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [pastEvents, setPastEvents] = useState<Event[]>([]);
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [upcomingResponse, pastResponse, newsResponse] = await Promise.all([
          api.get('/siteContent/events'),
          api.get('/siteContent/events'),
          api.get('/siteContent/news'),
        ]);
        setUpcomingEvents(upcomingResponse.data || []);
        setPastEvents(pastResponse.data || []);
        setNews(newsResponse.data || []);
      } catch (err) {
        setError('Failed to fetch event and news content.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const categories = [
    { id: 'all', name: 'Tous les événements' },
    { id: 'congress', name: 'Congrès' },
    { id: 'training', name: 'Formations' },
    { id: 'masterclass', name: 'Masterclass' },
    { id: 'workshop', name: 'Ateliers' }
  ];

  const filteredEvents = selectedCategory === 'all' 
    ? upcomingEvents 
    : upcomingEvents.filter(event => event.category === selectedCategory);

  const filteredPast = selectedCategory === 'all' 
    ? pastEvents 
    : pastEvents.filter(event => event.category === selectedCategory);

  if (loading) {
    return <div>Loading events and news...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
     <section className="relative bg-gradient-to-br from-gray-900 via-gray-400 to-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFB639]/20 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                  Événements & <span className="text-[#FFB639]">Actualités</span>
                </h1>
                <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Participez à nos événements exclusifs et restez informé des dernières actualités du football.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-all duration-300 transform hover:scale-105 shadow-lg">
                  <Ticket className="mr-2 h-5 w-5" />
                  Voir les événements
                </button>
                <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300">
                  <Bell className="mr-2 h-5 w-5" />
                  S'abonner aux actualités
                </button>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Événements exclusifs</span>
                </div>
                <div className="flex items-center">
                  <Star className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Experts reconnus</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img
                  src="https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg"
                  alt="Événement FORMACADEMY"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#FFB639] text-white p-6 rounded-xl shadow-lg">
                  <div className="text-2xl font-bold">500+</div>
                  <div className="text-sm">Participants/an</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-[#FFB639] text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Événements à venir
            </h2>
            <p className="text-xl text-gray-600">
              Rejoignez-nous lors de nos prochains événements exclusifs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {filteredEvents.map((event) => (
              <div key={event.id} className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 ${event.isPopular ? 'ring-2 ring-[#FFB639]' : ''}`}>
                {event.isPopular && (
                  <div className="bg-[#FFB639] text-white text-center py-2 text-sm font-semibold">
                    Événement phare
                  </div>
                )}
                
                <div className="relative">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-900">
                    {event.price}€
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(event.date).toLocaleDateString('fr-FR')}
                      {event.endDate && event.endDate !== event.date && (
                        <span> - {new Date(event.endDate).toLocaleDateString('fr-FR')}</span>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="h-4 w-4 mr-1" />
                      {event.attendees} places
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {event.title}
                  </h3>
                  
                  <div className="flex items-center mb-3 text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-1" />
                    {event.location} - {event.venue}
                  </div>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {event.description}
                  </p>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Intervenants:</h4>
                    <div className="flex flex-wrap gap-1">
                      {event.speakers?.map((speaker, index) => (
                        <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                          {speaker}
                        </span>
                      ))}
                    </div>
                  </div>

                  <ul className="space-y-1 mb-6">
                    {event.highlights.slice(0, 3).map((highlight, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-center">
                        <div className="w-1.5 h-1.5 bg-[#FFB639] rounded-full mr-2"></div>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to="/login"
                    className="w-full bg-[#FFB639] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#FFB639] transition-colors duration-300 text-center block"
                  >
                    Réserver ma place
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Events */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Événements passés
            </h2>
            <p className="text-xl text-gray-600">
              Retour sur nos derniers événements
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredPast.map((event) => (
              <div key={event.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-48 md:h-full object-cover"
                  />
                  
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <Star className="h-4 w-4 text-[#FFB639]  mr-1" />
                        <span className="text-sm font-medium text-gray-700">{event.rating}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {event.attendees} participants
                      </div>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {event.title}
                    </h3>
                    
                    <div className="flex items-center mb-3 text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(event.date).toLocaleDateString('fr-FR')}
                    </div>

                    <p className="text-gray-600 mb-4 text-sm">
                      {event.description}
                    </p>

                    <ul className="space-y-1 mb-4">
                      {event.highlights.map((highlight, index) => (
                        <li key={index} className="text-sm text-gray-600 flex items-center">
                          <div className="w-1.5 h-1.5 bg-[#FFB639] rounded-full mr-2"></div>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                    
                    <button className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors duration-300 text-sm">
                      Voir le résumé
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Actualités FORMACADEMY
            </h2>
            <p className="text-xl text-gray-600">
              Restez informé de nos dernières nouveautés
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map((article) => (
              <article key={article.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-48 object-cover"
                />
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm bg-[#FFB639]/15 text-[#FFB639] px-3 py-1 rounded-full font-medium">
                      {article.category}
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(article.date).toLocaleDateString('fr-FR')}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {article.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4">
                    {article.description}
                  </p>
                  
                  <button className="text-[#FFB639] font-semibold hover:text-black transition-colors duration-300 flex items-center">
                    Lire la suite
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-colors duration-300">
              Voir toutes les actualités
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Newsletter FORMACADEMY
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Recevez les nouveautés FORMACADEMY directement par email : événements, actualités, formations et bien plus.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <Bell className="h-5 w-5 text-[#FFB639] mr-2" />
                    Alertes événements en priorité
                  </li>
                  <li className="flex items-center">
                    <Star className="h-5 w-5 text-[#FFB639] mr-2" />
                    Actualités exclusives
                  </li>
                  <li className="flex items-center">
                    <Ticket className="h-5 w-5 text-[#FFB639] mr-2" />
                    Offres spéciales abonnés
                  </li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="text-xl font-bold text-gray-900 mb-4">
                  Inscription gratuite
                </h4>
                <form className="space-y-4">
                  <input
                    type="email"
                    placeholder="Votre adresse email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FFB639] focus:border-[#FFB639]"
                  />
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="newsletter-consent"
                      className="rounded border-gray-300 text-[#FFB639] shadow-sm focus:border-[#FFB639] focus:ring focus:ring-[#FFB639] focus:ring-opacity-50"
                    />
                    <label htmlFor="newsletter-consent" className="ml-2 text-sm text-gray-600">
                      J'accepte de recevoir la newsletter FORMACADEMY
                    </label>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-[#FFB639] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#FFB639] transition-colors duration-300"
                  >
                    S'abonner maintenant
                  </button>
                </form>
                <p className="text-xs text-gray-500 mt-3">
                  Désabonnement possible à tout moment. Vos données sont protégées selon le RGPD.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-black to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ne manquez plus aucun événement
          </h2>
          <p className="text-xl text-[#FFB639] mb-8 max-w-3xl mx-auto">
            Rejoignez notre communauté et participez aux événements qui façonnent l'avenir du football
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#FFB639] transition-all duration-300 transform hover:scale-105"
            >
              Rejoindre la communauté
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-black transition-all duration-300">
              Voir le calendrier
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShowcaseEvenements;
